import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { AdminLayout } from '../components/AdminLayout';
import { KPICard } from '../components/KPICard';
import { useUserGrowth } from '../hooks/useAdminStats';
import { useAdminFilter } from '../components/AdminSidebar';
import { useTheme } from '@/contexts/ThemeContext';
import { UserPlus, Users, TrendingUp, Globe, Calendar, RefreshCw } from 'lucide-react';

const COUNTRY_NAMES: Record<string, string> = {
  MA: '🇲🇦 Maroc',
  FR: '🇫🇷 France',
  ES: '🇪🇸 Espagne',
  BE: '🇧🇪 Belgique',
  DE: '🇩🇪 Allemagne',
  CA: '🇨🇦 Canada',
  CH: '🇨🇭 Suisse',
  GB: '🇬🇧 Royaume-Uni',
  IT: '🇮🇹 Italie',
  PT: '🇵🇹 Portugal',
  US: '🇺🇸 USA',
  NL: '🇳🇱 Pays-Bas',
  TN: '🇹🇳 Tunisie',
  DZ: '🇩🇿 Algérie',
  SN: '🇸🇳 Sénégal',
  CI: "🇨🇮 Côte d'Ivoire",
  LU: '🇱🇺 Luxembourg',
  AT: '🇦🇹 Autriche',
  IE: '🇮🇪 Irlande',
  SE: '🇸🇪 Suède',
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString('fr-FR');
}

export default function UserGrowthPage() {
  const { excludeAdmins } = useAdminFilter();
  const { data, isLoading, error, refetch } = useUserGrowth(excludeAdmins);
  const { isDarkMode } = useTheme();

  const signupsChartOption = useMemo(() => {
    if (!data?.signups_over_time) return {};
    const days = data.signups_over_time.map((d) => d.day);
    const counts = data.signups_over_time.map((d) => d.count);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      grid: { top: 20, right: 20, bottom: 30, left: 50 },
      xAxis: {
        type: 'category',
        data: days,
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: {
          color: isDarkMode ? '#9ca3af' : '#6b7280',
          fontSize: 10,
          formatter: (v: string) => v.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      series: [
        {
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: { color: '#16c47f', width: 3 },
          itemStyle: { color: '#16c47f' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#16c47f30' },
                { offset: 1, color: '#16c47f00' },
              ],
            },
          },
        },
      ],
    };
  }, [data, isDarkMode]);

  const sourcesChartOption = useMemo(() => {
    if (!data?.acquisition_sources) return {};
    const sources = data.acquisition_sources;
    const colors = [
      '#16c47f',
      '#8b5cf6',
      '#f59e0b',
      '#ef4444',
      '#3b82f6',
      '#ec4899',
      '#14b8a6',
      '#f97316',
    ];

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      series: [
        {
          type: 'pie',
          radius: ['45%', '75%'],
          center: ['50%', '50%'],
          data: sources.map((s, i) => ({
            name: s.source,
            value: s.count,
            itemStyle: { color: colors[i % colors.length] },
          })),
          label: {
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            fontSize: 11,
          },
          emphasis: {
            itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.1)' },
          },
        },
      ],
    };
  }, [data, isDarkMode]);

  if (isLoading) {
    return (
      <AdminLayout title="User Growth" subtitle="Croissance des utilisateurs">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
            >
              <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
              <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="User Growth" subtitle="Croissance des utilisateurs">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl hover:bg-red-200 transition"
          >
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="User Growth"
      subtitle="Croissance des utilisateurs"
      action={
        <button
          onClick={refetch}
          className="p-2 rounded-xl text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/10 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      }
    >
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <KPICard
          title="Nouveaux Aujourd'hui"
          value={formatNumber(data?.new_today ?? 0)}
          icon={UserPlus}
          gradient="from-emerald-500 to-emerald-600"
        />
        <KPICard
          title="Nouveaux Cette Semaine"
          value={formatNumber(data?.new_this_week ?? 0)}
          icon={Calendar}
          gradient="from-blue-500 to-blue-600"
        />
        <KPICard
          title="Nouveaux Ce Mois"
          value={formatNumber(data?.new_this_month ?? 0)}
          icon={Users}
          gradient="from-violet-500 to-violet-600"
        />
        <KPICard
          title="Moy. Inscriptions/Jour"
          value={String(data?.avg_daily_signups ?? 0)}
          icon={UserPlus}
          gradient="from-amber-500 to-amber-600"
        />
        <KPICard
          title="Taux de Croissance"
          value={`${data?.growth_rate ?? 0}%`}
          icon={TrendingUp}
          gradient="from-[#16c47f] to-emerald-600"
          change={data?.growth_rate}
        />
        <KPICard
          title="Top Source"
          value={data?.top_source ?? 'N/A'}
          icon={Globe}
          gradient="from-pink-500 to-pink-600"
        />
      </div>

      {/* Charts + Country Distribution */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Signups over time */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Inscriptions (90 jours)
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Évolution des inscriptions quotidiennes
          </p>
          {data?.signups_over_time?.length ? (
            <ReactECharts option={signupsChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>

        {/* Acquisition sources */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Sources d'Acquisition
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            D'où viennent les utilisateurs
          </p>
          {data?.acquisition_sources?.length ? (
            <ReactECharts option={sourcesChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>
      </div>

      {/* Country Distribution Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          Distribution par Pays
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Top 10 des pays d'origine</p>
        {data?.country_distribution?.length ? (
          <div className="space-y-3">
            {data.country_distribution.map((country, i) => {
              const total = data.country_distribution!.reduce((sum, c) => sum + c.count, 0);
              const pct = total > 0 ? (country.count / total) * 100 : 0;
              return (
                <div key={country.country_code} className="flex items-center gap-4">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-6 text-right">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white w-40">
                    {COUNTRY_NAMES[country.country_code] || country.country_code}
                  </span>
                  <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#16c47f] to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 w-12 text-right">
                    {country.count}
                  </span>
                  <span className="text-xs text-gray-400 w-12 text-right">{pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 dark:text-gray-500 text-sm text-center py-8">
            Aucune donnée disponible
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
